import { fmtDate, fmtTime } from "@/lib/dates";

// One place for "what do we call this role out loud" — the backend's own vocabulary (customer/
// admin/reviewer) leaks "reviewer" into the UI, where the rest of the app already says "advocate"
// (see AdvocatePages.jsx, backend/CLAUDE.md's own "advocate" portal naming).
const ROLE_LABEL = { admin: "Admin", customer: "Customer", reviewer: "Advocate" };

// Same three colours every avatar in this app has used since the flat-list version of this
// thread (TransactionDetail, AdvocatePages and the old admin relay view all agreed on this
// palette independently) — kept so switching to bubbles doesn't also quietly restyle whose
// colour means what.
const ROLE_COLOR = { admin: "#2D3B57", customer: "#2563EB", reviewer: "#CBA96C" };

const sameDay = (a, b) => a.toDateString() === b.toDateString();

/** "Today" / "Yesterday" / DD-MM-YYYY — the WhatsApp-style divider between days of messages. */
function dayLabel(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return fmtDate(ts);
}

function Bubble({ c, own, labelFor }) {
  // `labelFor` may return a falsy value to mean "no override for this one" (e.g. only reviewer
  // messages get anonymised on the customer's thread) — it is not required to handle every case.
  const label = (labelFor && labelFor(c)) || `${c.author_name} · ${ROLE_LABEL[c.author_role] || c.author_role}`;

  return (
    <div className={`flex gap-2 items-end ${own ? "justify-end" : "justify-start"}`}>
      {!own && (
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
             style={{ background: ROLE_COLOR[c.author_role] || "#6B7280" }}>
          {(c.author_name || "?")[0]}
        </div>
      )}
      <div
        data-testid={`comment-${c.id}`}
        className={`max-w-[78%] sm:max-w-[70%] rounded-2xl px-3 py-2 text-sm ${
          own
            ? "bg-[#CBA96C]/20 border border-[#CBA96C]/30 text-white rounded-br-sm"
            : "bg-white/[0.06] border border-white/10 text-white rounded-bl-sm"
        }`}
      >
        {!own && <div className="text-[11px] font-medium mb-0.5" style={{ color: ROLE_COLOR[c.author_role] || "#A0AABF" }}>{label}</div>}
        <div className="whitespace-pre-wrap break-words leading-relaxed">{c.text}</div>
        <div className="flex items-center gap-1.5 justify-end mt-1 -mb-0.5">
          {/* Quick-messages carry their own read state (reviewer_messages.read) — see
              AdvocatePages.jsx, where they're merged into this same thread so admin's broadcast
              and the two-way conversation read as one. */}
          {!own && c.unread && (
            <span className="text-[10px] text-[#60A5FA]" title="Unread">New</span>
          )}
          <span className="text-[10px] text-[#8B95A8]">{fmtTime(c.ts)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * A message thread as chat bubbles — own messages right, everyone else's left, grouped under
 * "Today"/"Yesterday"/date dividers, the same visual language across all three portals (admin's
 * case page, the customer's case page, the advocate's case page) rather than each hand-rolling
 * its own flat list of avatar rows.
 *
 * **"Own" is relative to `viewerRole`, not a fixed side.** Admin's `viewerRole="admin"` puts
 * admin's own replies on the right; the customer's own case page passes `viewerRole="customer"`,
 * so the *same* customer message that sat on the left in admin's view is on the right here — each
 * viewer sees their own words on the right, exactly like opening the same WhatsApp conversation
 * from either side of it. There is no per-message sharing control any more: customer, reviewer
 * and admin are all members of one thread, and every message is visible to all three as soon as
 * it's posted (backend/CLAUDE.md "Case messages are one shared thread").
 */
export default function ChatThread({ comments, viewerRole, labelFor, emptyText = "No messages yet." }) {
  if (!comments?.length) {
    return <div className="text-sm text-[#6B7280] text-center py-6">{emptyText}</div>;
  }
  let lastDay = null;
  return (
    <div className="flex flex-col gap-1.5 py-1">
      {comments.map((c) => {
        const day = dayLabel(c.ts);
        const showDivider = day !== lastDay;
        lastDay = day;
        return (
          <div key={c.id}>
            {showDivider && (
              <div className="flex justify-center my-2.5">
                <span className="text-[10px] uppercase tracking-wide text-[#6B7280] bg-white/[0.06] rounded-full px-3 py-1">{day}</span>
              </div>
            )}
            <Bubble c={c} own={c.author_role === viewerRole} labelFor={labelFor}/>
          </div>
        );
      })}
    </div>
  );
}
